/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  createRef,
  useCallback,
  useEffect,
  useState,
  useMemo,
} from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import MapResults from './MapResults';
import { Pagination } from '@italia/components/ItaliaTheme';
import Results from './Results';
import SearchBar from './SearchBar';
import SearchSorting from './SearchSorting';
import { Spinner } from 'design-react-kit/dist/design-react-kit';
import { changeDateFormat } from '@package/helpers';
import cx from 'classnames';
import { debounce } from 'lodash';
import { getFarmacia } from '@package/actions';
import { getVocabulary } from '@plone/volto/actions';

const vocabulary = 'auslfe.farmacie.aree_territoriali';

const Body = ({ isEditMode, data }) => {
  const dispatch = useDispatch();
  const resultsRef = createRef();
  const searchType = data?.search_type; // type of search, Ferie o Turni ('shifts' or 'vacations')
  const searchAreaTerritoriale = useMemo(
    () => data?.search_area_territoriale ?? [],
    [data?.search_area_territoriale],
  ); // set from Sidebar ["1", "2"]
  const b_size = 10; // number of page results to show
  const [currentPage, setCurrentPage] = useState(1);

  const [sorting, setSorting] = useState('comune');
  const [filters, setFilters] = useState({});
  const [filtersOptions, setFiltersOptions] = useState({
    aree_territoriali: [],
    comuni: [],
    lista_localita: [],
  });

  const [results, setResults] = useState([]);
  const [resultsPage, setResultsPage] = useState([]);

  const loading = useSelector((state) => {
    return state.farmacia?.loading || false;
  });
  const searchFarmacia = useSelector((state) => {
    return state.farmacia?.result;
  });

  const listAreeTerritoriali = useSelector(
    (state) => state.vocabularies?.[vocabulary]?.items ?? [],
    shallowEqual,
  );

  useEffect(() => {
    dispatch(
      getVocabulary({
        vocabNameOrURL: vocabulary,
      }),
    );
  }, [dispatch]);

  /* Initial search */
  const doSearch = () => {
    dispatch(getFarmacia());
  };

  useEffect(() => {
    doSearch();
  }, [searchType]);

  /* Search with filters */
  const doFilter = (filters, items, searchType) => {
    let newResults = items ?? [];

    // Filtro testo libero
    if (filters.searchableText?.length > 0) {
      newResults = items.filter(
        (item) =>
          item.title
            .toLowerCase()
            .includes(filters.searchableText.toLowerCase()) ||
          item.comune
            .toLowerCase()
            .includes(filters.searchableText.toLowerCase()) ||
          item.localita
            .toLowerCase()
            .includes(filters.searchableText.toLowerCase()),
      );
    }

    // Turni - filtro Data
    if (searchType === 'shifts' && filters.date && items?.length > 0) {
      const inputDate = Date.parse(filters.date);

      newResults = newResults.filter((item) => {
        const checkTurno = item?.turni?.map((turno) => {
          // anche se il turno ha un orario il controllo e su inizio/fine giornata
          const turnoDal = Date.parse(changeDateFormat(turno.dal, '00:00'));
          const turnoAl = Date.parse(changeDateFormat(turno.al, '23:59'));
          return turnoDal <= inputDate && turnoAl >= inputDate;
        });
        return checkTurno && checkTurno.includes(true);
      });
    }

    // Turni - filtro Area Territoriale
    if (filters.area_territoriale) {
      newResults = newResults.filter(
        // eslint-disable-next-line eqeqeq
        (item) => item.area_territoriale === filters.area_territoriale.value,
      );
    } else if (searchAreaTerritoriale?.length > 0) {
      //filtro solo per le aree territoriali selezionate nella sidebar, se non è stato selezionato un filtro specifico per l'area territoriale
      newResults = newResults.filter((item) =>
        searchAreaTerritoriale.includes(item.area_territoriale),
      );
    }

    // Ferie - filtro Comune
    if (filters.comune) {
      newResults = newResults.filter(
        (item) =>
          item.comune.toLowerCase() === filters.comune.value.toLowerCase(),
      );
    }

    // Ferie - filtro Località
    if (filters.localita) {
      newResults = newResults.filter(
        (item) =>
          item.localita.toLowerCase() === filters.localita.value.toLowerCase(),
      );
    }

    // set results
    setResults(newResults);
  };

  const doDebouncedFilter = useCallback(debounce(doFilter, 400), []);

  // Filtra un array di oggetti dato l'array dei valori selezionati nelle aree_territoriali dalla sidebar
  const filterAreeTerritoriali = (aree) => {
    return searchAreaTerritoriale?.length > 0
      ? aree.filter((obj) => searchAreaTerritoriale.includes(obj.value))
      : aree;
  };

  useEffect(() => {
    let options = { ...filtersOptions, comuni: [], lista_localita: [] };
    let farmacie = [];
    let comuniOptions = [];
    let localitaOptions = [];

    if (searchFarmacia.items) {
      // set results
      farmacie = searchFarmacia.items;

      comuniOptions = [
        ...new Set(
          (searchAreaTerritoriale.length > 0
            ? farmacie.filter((item) =>
                searchAreaTerritoriale.includes(item.area_territoriale),
              )
            : farmacie
          )
            .map((item) => item.comune)
            .sort(),
        ),
      ];
      localitaOptions = [
        ...new Set(farmacie.map((item) => item.localita).sort()),
      ];
    }
    createSelectOptions(comuniOptions, options.comuni);
    createSelectOptions(localitaOptions, options.lista_localita);

    setFiltersOptions(options);

    setResults(sortByInput(farmacie, sorting));

    // set defaults date and change results
    setFilters({
      ...filters,
      // in realtà "date" dovrebbe essere una data e non un datetime, per ora lascio così,
      // (old) modificando però da una datetime localtime ad una dateime UTC, visto che dal/al nei
      //       calcoli fatti qui sono in UTC
      // (new) riportato a localtme, perchè ora le date sono in localtime
      // TODO andrebbe usata la timezone definita sul server anzichè la localtime del browser, che potrebbe
      //      non essere corretta
      date: new Date().toISOString(),
      // date: new Date(
      //   new Date().getTime() - new Date().getTimezoneOffset() * 60000,
      // ).toISOString(),
      // area_territoriale:
      //   searchAreaTerritoriale?.length === 1
      //     ? options.aree_territoriali[0]
      //     : null,
    });
  }, [searchFarmacia]);

  const setAreaTerritorialeFilters = () => {
    // create filters Area Territoriale, Comune and Località
    const options_aree_territoriali =
      filterAreeTerritoriali(listAreeTerritoriali);

    setFiltersOptions({
      ...filtersOptions,
      aree_territoriali: options_aree_territoriali,
    });

    if (searchAreaTerritoriale?.length === 1) {
      setFilters({
        ...filters,
        area_territoriale: options_aree_territoriali[0],
      });
    }
  };
  /*
  -- Impostazione del valore di default per il filtro dell'Area Territoriale. --
  Se nella sidebar è selezionata un'area territoriale specifica, viene impostato il filtro corrispondente, altrimenti viene resettato a null.
  Se nella sidebar sono selezionate più aree territoriali, il filtro viene resettato a null per permettere all'utente di scegliere una delle aree territoriali scelte nella sidebar.
  */
  useEffect(() => {
    // create filters Area Territoriale, Comune and Località

    setAreaTerritorialeFilters();
  }, [listAreeTerritoriali]);

  useEffect(() => {
    setAreaTerritorialeFilters();
  }, [searchAreaTerritoriale]);

  // check if select Comune is empty or not
  const checkClearComune = (inputComune) => {
    if (searchFarmacia.items?.length > 0) {
      let resultsByComune = searchFarmacia.items;
      let options = { comuni: [], lista_localita: [] };
      let localitaOptions = [];

      if (inputComune === null) {
        localitaOptions = [
          ...new Set(searchFarmacia.items.map((item) => item.localita).sort()),
        ];
        createSelectOptions(localitaOptions, options.lista_localita);
        setFiltersOptions({
          ...filtersOptions,
          lista_localita: options.lista_localita,
        });
      } else {
        resultsByComune = resultsByComune.filter(
          (item) =>
            item.comune.toLowerCase() === inputComune.value.toLowerCase(),
        );
        localitaOptions = [
          ...new Set(resultsByComune.map((item) => item.localita).sort()),
        ];
        createSelectOptions(localitaOptions, options.lista_localita);
        setFiltersOptions({
          ...filtersOptions,
          lista_localita: options.lista_localita,
        });
      }
    }
  };

  // function to create object select options
  function createSelectOptions(arrayOptions, selectOptions) {
    // eslint-disable-next-line no-unused-expressions
    arrayOptions?.forEach((option) => {
      selectOptions.push({
        value: option.toLowerCase(),
        label: option,
      });
    });
  }

  // pagination function
  function onPaginationChange(e, { activePage }) {
    resultsRef.current.scrollIntoView({ behavior: 'smooth' });
    const current = activePage?.children ?? 1;
    setCurrentPage(current);
  }

  // sort function
  const sortByInput = (items, value) => {
    items.sort((a, b) => {
      if (a[value] < b[value]) {
        return -1;
      }
      if (a[value] > b[value]) {
        return 1;
      }
      return 0;
    });
  };

  // trigger for filters
  useEffect(() => {
    if (searchFarmacia?.items) {
      doDebouncedFilter(filters, searchFarmacia.items, searchType);
    }
  }, [filters]);

  useEffect(() => {
    setCurrentPage(1);
  }, [results]);

  //quando viene modificato il criterio di ordinamento da parte dell'utente
  useEffect(() => {
    if (results?.length > 0) {
      const sortedResults = [...results];
      sortByInput(sortedResults, sorting);
      setResults(sortedResults);
    }
  }, [sorting]);

  // Popola la variabile resultsPage in base alla pagina corrente e al numero di risultati per pagina (b_size), ordinando prima i risultati in base al criterio di ordinamento selezionato.
  useEffect(() => {
    if (results?.length > 0) {
      const sortedResults = [...results];
      sortByInput(sortedResults, sorting); //ordina i risultati in base al criterio di ordinamento selezionato

      let start = b_size * (currentPage - 1);
      let end = b_size * currentPage;
      setResultsPage(sortedResults.slice(start, end));
    } else {
      setResultsPage([]);
    }
  }, [currentPage, results]);

  return (
    <div
      className={cx('', {
        'public-ui': isEditMode,
      })}
    >
      <div className="block-search-farmacia">
        <SearchBar
          isEditMode={isEditMode}
          setFilters={setFilters}
          searchType={searchType}
          filters={filters}
          options={filtersOptions}
          checkClearComune={checkClearComune}
        />
        <SearchSorting
          setSorting={setSorting}
          sorting={sorting}
          isEditMode={isEditMode}
        />

        {!loading ? (
          <>
            {/* Risultati tabella */}
            <Results
              items={resultsPage}
              dateFilter={filters.date}
              isEditMode={isEditMode}
              searchType={searchType}
              resRef={resultsRef}
            />
            {/* Paginazione */}
            {results?.length > b_size && (
              <div className="pagination-wrapper">
                <Pagination
                  key={new Date().getTime()}
                  activePage={currentPage}
                  totalPages={Math.ceil(results?.length / b_size)}
                  onPageChange={onPaginationChange}
                />
              </div>
            )}
            {/* Mappa dei risultati */}
            {data?.show_map && <MapResults items={resultsPage} />}
          </>
        ) : (
          <div className="d-flex justify-content-center mt-3">
            <Spinner active />
          </div>
        )}
      </div>
    </div>
  );
};

export default Body;
